<template>
  <Tooltip.Root :disabled="!tzDate">
    <Tooltip.Trigger v-if="tzDate" v-bind="$attrs">
      <time
        v-if="tzDate"
        :datetime="tzDate.toISOString()"
        class="truncate tabular-nums"
      >
        <span
          v-if="
            variant === 'datetime-emphasized' &&
            formatted.split(' ').length === 3
          "
          class="space-x-1 truncate"
        >
          <span class="text-muted-foreground">
            {{ formatted.split(" ")[0] }}
          </span>
          <span class="">
            {{ formatted.split(" ")[1] }}
          </span>
          <span class="text-muted-foreground">
            {{ formatted.split(" ")[2] }}
          </span>
        </span>
        <template v-else>
          {{ formatted }}
        </template>
      </time>
    </Tooltip.Trigger>
    <span v-else v-bind="$attrs">—</span>

    <Tooltip.Content v-if="tzDate" width-class="max-w-72">
      <p
        class="mb-1 text-xs font-bold uppercase text-gray-300 dark:text-gray-200"
      >
        Time conversion
      </p>

      <div class="grid w-full grid-cols-[1fr,3fr] whitespace-nowrap">
        <div class="pr-4 text-left text-xs">
          {{ formatTimezoneToLocation(newsletterTz || systemTimezone) }}
        </div>
        <div class="text-right text-xs font-bold tabular-nums">
          {{
            formatDateTimeNumeric(
              tzDate.withTimeZone(newsletterTz || systemTimezone)
            )
          }}
        </div>

        <template v-if="newsletterTz && systemTimezone !== newsletterTz">
          <div class="text-left text-xs">
            {{ formatTimezoneToLocation(systemTimezone) }}
          </div>
          <div class="text-right text-xs font-bold tabular-nums">
            {{ formatDateTimeNumeric(tzDate.withTimeZone(systemTimezone)) }}
          </div>
        </template>

        <template
          v-if="!(systemTimezone + (newsletterTz || '')).includes('UTC')"
        >
          <div class="text-left text-xs">UTC</div>
          <div class="text-right text-xs font-bold tabular-nums">
            {{ formatDateTimeNumeric(tzDate.withTimeZone("Etc/UTC")) }}
          </div>
        </template>
      </div>
    </Tooltip.Content>
  </Tooltip.Root>
</template>

<script lang="ts" setup>
import { TZDate } from "@date-fns/tz";
import { computed } from "vue";

import { formatTimezoneToLocation } from "@/components/Layout/ListView/timezone-conversion";
import Tooltip from "@/design_system/Tooltip";
import {
  formatDateNumeric,
  formatDateTimeNumeric,
  formatRelativeTime,
  formatTime,
  systemTimezone,
} from "@/lib/time";
import { useNewsletter } from "@/store/stores/account";

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    value: string | Date | TZDate | undefined;
    variant?:
      | "date"
      | "datetime"
      | "datetime-emphasized"
      | "time"
      | "relative"
      | "relative-narrow";
  }>(),
  {
    variant: "date",
  }
);

const newsletter = useNewsletter();

const newsletterTz = computed(() => newsletter.value.timezone);

const tzDate = computed(() => {
  const value = props.value;

  if (!value) return undefined;
  if (value instanceof TZDate) return value;

  // types doesn't like it if it's mixed with the string param variant
  if (value instanceof Date) return new TZDate(value);

  return new TZDate(value);
});

const formatted = computed(() => {
  if (!tzDate.value) return "";

  switch (props.variant) {
    case "relative":
      return formatRelativeTime(tzDate.value);
    case "relative-narrow":
      return formatRelativeTime(tzDate.value, undefined, "narrow");
    case "datetime":
    case "datetime-emphasized":
      return formatDateTimeNumeric(tzDate.value);
    case "time":
      return formatTime(tzDate.value);
    case "date":
      return formatDateNumeric(tzDate.value);
  }

  return "";
});
</script>
